import { useEffect, useState } from "react"
import { SpinnerText } from "../ui/Spinner"
import DesignUploadBox from "../ui/DesignUploadBox"
import InfoBox from "../ui/InfoBox"
import { FiEdit2 } from "react-icons/fi"
import DesignNameDialog from "../ui/DesignNameDialog"
import {
  ProductIndicationTerm,
  searchProductIndications,
  validateProductIndications
} from "../../services/productIndicationService"
import {
  DesignerSearchResult,
  searchDesigners
} from "../../services/designerService"
import {
  DesignDetails,
  DesignSearchResultItem,
  getDesignDetails,
  searchDesigns
} from "../../services/designSearchService"
import { uploadDocument } from "../../services/documentService"
import { formatApiError } from "../../utils/formatApiError"
import ProductIndicationSection from "./ProductIndicationSection"
import DescriptionSection from "./DescriptionSection"
import DesignerDetailsSection from "./DesignerDetailsSection"
import DesignClaimsSection from "./DesignClaimsSection"
import WipoDasSection from "./WipoDasSection"
import DefermentSection from "./DefermentSection"
import SavedDesignList from "./SavedDesignList"
import SectionHeader from "../ui/SectionHeader"

type DesignViewUploadState = {
  status: "uploading" | "uploaded" | "error"
  euipoDocumentId?: string
  originalFileName?: string
  error?: string
}

export type SavedDesignCard = {
  id: string
  name: string
  previewUrls: string[]
  currentPreviewIndex: number
  viewCount: number
  productLabel: string
  defermentLabel: string
  claimsCount: number
  payload: any
}

const MAX_DESIGNS_PER_APPLICATION = 100

function getDesignFileKey(file: File) {
  return `${file.name}-${file.size}-${file.lastModified}`
}

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => resolve(String(reader.result || ""))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

function formatDesignLabel(design: any, index: number) {
  return design.name || `Design ${index + 1}`
}

function formatProductIndicationLabel(product: any) {
  return `${String(product.classNumber).padStart(2, "0")}.${String(product.subClassNumber).padStart(2, "0")} ${product.text || product.terms?.[0] || "—"}`
}

function formatProductIndicationsLabel(productIndications: any[] = []) {
  return productIndications.length
    ? productIndications.map(formatProductIndicationLabel).join(", ")
    : "-"
}

function groupProductIndications(productIndications: ProductIndicationTerm[]) {
  const groups: Record<string, any> = {}

  for (const term of productIndications) {
    const key = `${term.classNumber}-${term.subClassNumber}`
    if (!groups[key]) {
      groups[key] = {
        classNumber: term.classNumber,
        subClassNumber: term.subClassNumber,
        terms: [],
        language: "en"
      }
    }

    if (!groups[key].terms.includes(term.text)) {
      groups[key].terms.push(term.text)
    }
  }

  return Object.values(groups)
}

export function mapLoadedDesignToCard(design: any, index: number): SavedDesignCard {
  const previewUrls =
    design.views
      ?.map((view: any) => view.previewDataUrl || "")
      .filter(Boolean) || []
  const productLabel = formatProductIndicationsLabel(design.productIndications || [])

  return {
    id: design._id,
    name: formatDesignLabel(design, index),
    previewUrls,
    currentPreviewIndex: 0,
    viewCount: design.views?.length || 0,
    productLabel,
    defermentLabel: design.publicationDefermentIndicator ? "Yes" : "No",
    claimsCount: design.priorities?.length || 0,
    payload: {
      name: design.name || formatDesignLabel(design, index),
      views: design.views || [],
      productIndications: design.productIndications || [],
      priorities: design.priorities || [],
      description: design.description || undefined,
      publicationDefermentIndicator: !!design.publicationDefermentIndicator,
      publicationAfterDefermentPeriodIndicator:
        !!design.publicationAfterDefermentPeriodIndicator,
      claimWipoDasIndicator: !!design.claimWipoDasIndicator,
      designerWaiverIndicator: !!design.designerWaiverIndicator,
      designers: (design.designers || []).map((item: any) => ({
        designerName: item.designerName,
        euipoDesignerIdentifier: item.euipoDesignerIdentifier || undefined
      }))
    }
  }
}

export default function DesignsSection({
  onChange,
  onDraftsChange,
  initialDesigns,
  disabled = false
}: {
  onChange: (value: boolean) => void
  onDraftsChange: (designs: SavedDesignCard[]) => void
  initialDesigns: SavedDesignCard[]
  disabled?: boolean
}) {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [designName, setDesignName] = useState("Design 1")
  const [isNameModalOpen, setIsNameModalOpen] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [designViewUploads, setDesignViewUploads] = useState<Record<string, DesignViewUploadState>>({})
  const [designViewUploadError, setDesignViewUploadError] = useState("")
  const [savingDesign, setSavingDesign] = useState(false)
  const [savedDesigns, setSavedDesigns] = useState<SavedDesignCard[]>(initialDesigns)
  const [designLimitError, setDesignLimitError] = useState("")
  const [editingSavedDesignId, setEditingSavedDesignId] = useState<string | null>(null)
  const [editingSavedDesignName, setEditingSavedDesignName] = useState("")
  const [deferment, setDeferment] = useState<"yes" | "no" | "">("")
  const [productIndication, setProductIndication] = useState("")
  const [productIndicationResults, setProductIndicationResults] = useState<ProductIndicationTerm[]>([])
  const [selectedProductIndication, setSelectedProductIndication] = useState<ProductIndicationTerm | null>(null)
  const [addedProductIndications, setAddedProductIndications] = useState<ProductIndicationTerm[]>([])
  const [productIndicationValidation, setProductIndicationValidation] = useState<Record<string, boolean | null>>({})
  const [productIndicationLoading, setProductIndicationLoading] = useState(false)
  const [productIndicationError, setProductIndicationError] = useState("")
  const [designerDetailsOpen, setDesignerDetailsOpen] = useState(false)
  const [designerMode, setDesignerMode] = useState<"database" | "new" | "waiver" | null>(null)
  const [designerDatabaseOpen, setDesignerDatabaseOpen] = useState(false)
  const [designerSearch, setDesignerSearch] = useState("")
  const [designerSearchResults, setDesignerSearchResults] = useState<DesignerSearchResult[]>([])
  const [selectedDesigner, setSelectedDesigner] = useState<DesignerSearchResult | null>(null)
  const [designerSearchLoading, setDesignerSearchLoading] = useState(false)
  const [designerSearchError, setDesignerSearchError] = useState("")
  const [designerName, setDesignerName] = useState("")
  const [designers, setDesigners] = useState<Array<{ id: string; name: string }>>([])
  const [editingDesignerIndex, setEditingDesignerIndex] = useState<number | null>(null)
  const [editingDesignerName, setEditingDesignerName] = useState("")
  const [descriptionOpen, setDescriptionOpen] = useState(false)
  const [description, setDescription] = useState("")
  const [claimsOpen, setClaimsOpen] = useState(false)
  const [priorityOptionsOpen, setPriorityOptionsOpen] = useState(false)
  const [priorityEntryMode, setPriorityEntryMode] = useState<"wipo" | "manual" | "database" | null>(null)
  const [transferDocumentChecked, setTransferDocumentChecked] = useState(false)
  const [transferDocumentFile, setTransferDocumentFile] = useState<File | null>(null)
  const [transferDocumentError, setTransferDocumentError] = useState("")
  const [databasePriorityCountry, setDatabasePriorityCountry] = useState("")
  const [databasePriorityId, setDatabasePriorityId] = useState("")
  const [databasePriorityResults, setDatabasePriorityResults] = useState<DesignSearchResultItem[]>([])
  const [databasePrioritySelected, setDatabasePrioritySelected] = useState<DesignDetails | null>(null)
  const [databasePriorityOpen, setDatabasePriorityOpen] = useState(false)
  const [databasePriorityLoading, setDatabasePriorityLoading] = useState(false)
  const [databasePriorityError, setDatabasePriorityError] = useState("")
  const [manualPriorityCountry, setManualPriorityCountry] = useState("")
  const [manualPriorityDate, setManualPriorityDate] = useState("")
  const [manualPriorityNumber, setManualPriorityNumber] = useState("")
  const [manualPriorityFile, setManualPriorityFile] = useState<File | null>(null)
  const [manualPriorityFileError, setManualPriorityFileError] = useState("")
  const [priorityClaims, setPriorityClaims] = useState<any[]>([])
  const [addingPriority, setAddingPriority] = useState(false)
  const [addPriorityError, setAddPriorityError] = useState("")
  const [wipoDasOpen, setWipoDasOpen] = useState(false)
  const [wipoDasAccepted, setWipoDasAccepted] = useState(false)


  const designViewUploadStates = uploadedFiles.map(file =>
    designViewUploads[getDesignFileKey(file)]
  )
  const hasDesignViewUploadInProgress = designViewUploadStates.some(
    upload => upload?.status === "uploading"
  )
  const hasDesignViewUploadError = designViewUploadStates.some(
    upload => upload?.status === "error"
  )
  const areDesignViewsUploaded =
    uploadedFiles.length > 0 &&
    designViewUploadStates.every(upload =>
      upload?.status === "uploaded" && upload.euipoDocumentId
    )
  const canAddDesign =
    areDesignViewsUploaded &&
    addedProductIndications.length > 0 &&
    !savingDesign &&
    !hasDesignViewUploadInProgress &&
    !addingPriority &&
    !hasDesignViewUploadError
  const hasReachedDesignLimit = savedDesigns.length >= MAX_DESIGNS_PER_APPLICATION

  const formatProductIndication = (term: ProductIndicationTerm) =>
    `${String(term.classNumber).padStart(2, "0")} ${String(term.subClassNumber).padStart(2, "0")} ${term.text}`

  const getProductIndicationKey = (term: ProductIndicationTerm) =>
    `${term.text}|${term.classNumber}|${term.subClassNumber}`

  const resetNewDesignForm = (nextDesignName?: string) => {
    setIsNameModalOpen(false)
    setUploadedFiles([])
    setDesignViewUploads({})
    setDesignViewUploadError("")
    setDeferment("")
    setProductIndication("")
    setProductIndicationResults([])
    setSelectedProductIndication(null)
    setAddedProductIndications([])
    setProductIndicationValidation({})
    setProductIndicationLoading(false)
    setProductIndicationError("")
    setDesignerDetailsOpen(false)
    setDesignerMode(null)
    setDesignerDatabaseOpen(false)
    setDesignerSearch("")
    setDesignerSearchResults([])
    setSelectedDesigner(null)
    setDesignerSearchLoading(false)
    setDesignerSearchError("")
    setDesignerName("")
    setDesigners([])
    setEditingDesignerIndex(null)
    setEditingDesignerName("")
    setDescriptionOpen(false)
    setDescription("")
    setClaimsOpen(false)
    setPriorityOptionsOpen(false)
    setPriorityEntryMode(null)
    setTransferDocumentChecked(false)
    setTransferDocumentFile(null)
    setTransferDocumentError("")
    setAddPriorityError("")
    setDatabasePriorityCountry("")
    setDatabasePriorityId("")
    setDatabasePriorityResults([])
    setDatabasePrioritySelected(null)
    setDatabasePriorityOpen(false)
    setDatabasePriorityLoading(false)
    setDatabasePriorityError("")
    setManualPriorityCountry("")
    setManualPriorityDate("")
    setManualPriorityNumber("")
    setManualPriorityFile(null)
    setManualPriorityFileError("")
    setPriorityClaims([])
    setAddingPriority(false)
    setAddPriorityError("")
    setWipoDasOpen(false)
    setWipoDasAccepted(false)
    setDesignLimitError("")
    setDesignName(nextDesignName || `Design ${savedDesigns.length + 1}`)
  }

  useEffect(() => {
    onDraftsChange(savedDesigns)
  }, [savedDesigns, onDraftsChange])

  useEffect(() => {
    setSavedDesigns(initialDesigns)
  }, [initialDesigns])

  useEffect(() => {
    const handleResetAll = () => {
      setIsFormOpen(false)
      setIsNameModalOpen(false)
      setUploadedFiles([])
      setDesignViewUploads({})
      setDesignViewUploadError("")
      setSavingDesign(false)
      setSavedDesigns([])
      setDesignLimitError("")
      setEditingSavedDesignId(null)
      setEditingSavedDesignName("")
      setDeferment("")
      setProductIndication("")
      setProductIndicationResults([])
      setSelectedProductIndication(null)
      setAddedProductIndications([])
      setProductIndicationValidation({})
      setProductIndicationLoading(false)
      setProductIndicationError("")
      setDesignerDetailsOpen(false)
      setDesignerMode(null)
      setDesignerDatabaseOpen(false)
      setDesignerSearch("")
      setDesignerSearchResults([])
      setSelectedDesigner(null)
      setDesignerSearchLoading(false)
      setDesignerSearchError("")
      setDesignerName("")
      setDesigners([])
      setEditingDesignerIndex(null)
      setEditingDesignerName("")
      setDescriptionOpen(false)
      setDescription("")
      setClaimsOpen(false)
      setPriorityOptionsOpen(false)
      setPriorityEntryMode(null)
      setTransferDocumentChecked(false)
      setTransferDocumentFile(null)
      setTransferDocumentError("")
      setDatabasePriorityCountry("")
      setDatabasePriorityId("")
      setDatabasePriorityResults([])
      setDatabasePrioritySelected(null)
      setDatabasePriorityOpen(false)
      setDatabasePriorityLoading(false)
      setDatabasePriorityError("")
      setManualPriorityCountry("")
      setManualPriorityDate("")
      setManualPriorityNumber("")
      setManualPriorityFile(null)
      setManualPriorityFileError("")
      setPriorityClaims([])
      setAddingPriority(false)
      setAddPriorityError("")
      setWipoDasOpen(false)
      setWipoDasAccepted(false)
    }

    window.addEventListener("resetAll", handleResetAll as EventListener)
    return () => window.removeEventListener("resetAll", handleResetAll as EventListener)
  }, [])

  useEffect(() => {
    const term = productIndication.trim()

    if (
      !term ||
      (selectedProductIndication &&
        formatProductIndication(selectedProductIndication) === productIndication)
    ) {
      setProductIndicationResults([])
      setProductIndicationLoading(false)
      setProductIndicationError("")
      return
    }

    const timeout = window.setTimeout(async () => {
      try {
        setProductIndicationLoading(true)
        setProductIndicationError("")
        const results = await searchProductIndications(term, "en")
        setProductIndicationResults(results)
      } catch {
        setProductIndicationResults([])
        setProductIndicationError("Product indications could not be loaded.")
      } finally {
        setProductIndicationLoading(false)
      }
    }, 250)

    return () => window.clearTimeout(timeout)
  }, [productIndication, selectedProductIndication])

  useEffect(() => {
    if (!addedProductIndications.length) {
      setProductIndicationValidation({})
      return
    }

    setProductIndicationValidation(prev => {
      return addedProductIndications.reduce((map, term) => {
        const key = getProductIndicationKey(term)
        map[key] = prev[key] ?? null
        return map
      }, {} as Record<string, boolean | null>)
    })

    const timeout = window.setTimeout(async () => {
      try {
        const result = await validateProductIndications(addedProductIndications, "en")
        const map: Record<string, boolean | null> = {}

        for (const group of result.productIndications ?? []) {
          for (const term of group.terms ?? []) {
            map[`${term.text}|${group.classNumber}|${group.subClassNumber}`] =
              term.harmonized ?? null
          }
        }

        setProductIndicationValidation(map)
      } catch (error) {
        console.error("Fehler bei der Überprüfung der Erzeugnisangaben:", error)
      }
    }, 250)

    return () => window.clearTimeout(timeout)
  }, [addedProductIndications])

  useEffect(() => {
    const query = designerSearch.trim()

    if (designerMode !== "database" || !query || selectedDesigner?.name === query) {
      setDesignerSearchResults([])
      setDesignerSearchLoading(false)
      setDesignerSearchError("")
      return
    }

    const timeout = window.setTimeout(async () => {
      try {
        setDesignerSearchLoading(true)
        setDesignerSearchError("")
        const results = await searchDesigners(query)
        setDesignerSearchResults(results)
        setDesignerDatabaseOpen(true)
      } catch {
        setDesignerSearchResults([])
        setDesignerSearchError("Designers could not be loaded.")
        setDesignerDatabaseOpen(true)
      } finally {
        setDesignerSearchLoading(false)
      }
    }, 250)

    return () => window.clearTimeout(timeout)
  }, [designerMode, designerSearch, selectedDesigner])

  useEffect(() => {
    const query = databasePriorityId.trim()

    if (priorityEntryMode !== "database" || !query || databasePrioritySelected?.designNumber === query) {
      setDatabasePriorityResults([])
      setDatabasePriorityOpen(false)
      setDatabasePriorityLoading(false)
      setDatabasePriorityError("")
      return
    }

    const timeout = window.setTimeout(async () => {
      try {
        setDatabasePriorityLoading(true)
        setDatabasePriorityError("")
        const results = await searchDesigns(query)
        setDatabasePriorityResults(results)
        setDatabasePriorityOpen(true)
      } catch {
        setDatabasePriorityResults([])
        setDatabasePriorityError("Designs could not be loaded.")
        setDatabasePriorityOpen(true)
      } finally {
        setDatabasePriorityLoading(false)
      }
    }, 250)

    return () => window.clearTimeout(timeout)
  }, [priorityEntryMode, databasePriorityId, databasePrioritySelected])

  const handleOpen = () => {
    if (disabled) return

    if (hasReachedDesignLimit) {
      setDesignLimitError(
        `You can add up to ${MAX_DESIGNS_PER_APPLICATION} designs per application.`
      )
      return
    }

    resetNewDesignForm(`Design ${savedDesigns.length + 1}`)
    setIsFormOpen(true)
    onChange(savedDesigns.length > 0)
  }

  const handleCancel = () => {
    setIsFormOpen(false)
    resetNewDesignForm(designName)
    setSavingDesign(false)
    onChange(savedDesigns.length > 0)
  }

  const uploadDesignView = async (file: File) => {
    const key = getDesignFileKey(file)

    setDesignViewUploads(prev => ({
      ...prev,
      [key]: { status: "uploading" }
    }))

    try {
      const result = await uploadDocument(file, "DESIGN_VIEW")

      setDesignViewUploads(prev => ({
        ...prev,
        [key]: {
          status: "uploaded",
          euipoDocumentId: result.euipoDocumentId,
          originalFileName: result.originalFileName
        }
      }))
      setDesignViewUploadError("")
    } catch (error) {
      const message = formatApiError(
        error,
        "The design view could not be uploaded. Please try again."
      )
      setDesignViewUploads(prev => ({
        ...prev,
        [key]: {
          status: "error",
          error: message
        }
      }))
      setDesignViewUploadError(message)
    }
  }

  const handleDesignFilesSelect = (files: File[]) => {
    if (disabled) return

    const nextKeys = new Set(files.map(getDesignFileKey))

    setDesignViewUploads(prev =>
      Object.entries(prev).reduce((map, [key, value]) => {
        if (nextKeys.has(key)) {
          map[key] = value
        }
        return map
      }, {} as Record<string, DesignViewUploadState>)
    )

    const previousKeys = new Set(uploadedFiles.map(getDesignFileKey))
    const filesToUpload = files.filter(file => {
      const key = getDesignFileKey(file)
      return !previousKeys.has(key) && designViewUploads[key]?.status !== "uploaded"
    })

    setUploadedFiles(files)
    filesToUpload.forEach(uploadDesignView)
  }

  const handleAdd = async () => {
    if (disabled) return

    if (hasReachedDesignLimit) {
      setDesignLimitError(
        `You can add up to ${MAX_DESIGNS_PER_APPLICATION} designs per application.`
      )
      return
    }

    if (!canAddDesign) return

    const groupedProductIndications = groupProductIndications(addedProductIndications)
    const productLabel = formatProductIndicationsLabel(addedProductIndications)

    const draftId = crypto.randomUUID()
    const previewUrls = await Promise.all(
      uploadedFiles.map(file =>
        file.type.startsWith("image/") ? readFileAsDataUrl(file) : Promise.resolve("")
      )
    )
    setSavedDesigns(prev => [
      ...prev,
      {
        id: draftId,
        name: designName,
        previewUrls,
        currentPreviewIndex: 0,
        viewCount: uploadedFiles.length,
        productLabel,
        defermentLabel: deferment === "yes" ? "Yes" : "No",
        claimsCount: priorityClaims.length,
        payload: {
          name: designName,
          views: uploadedFiles.map((file, index) => {
            const upload = designViewUploads[getDesignFileKey(file)]
            return {
              fileName: file.name,
              fileType: file.type || file.name.split(".").pop() || "",
              fileSize: file.size,
              euipoDocumentId: upload?.euipoDocumentId,
              protectedView: true,
              previewDataUrl: previewUrls[index] || ""
            }
          }),
          productIndications: groupedProductIndications,
          priorities: priorityClaims,
          description: description.trim() || undefined,
          publicationDefermentIndicator: deferment === "yes",
          publicationAfterDefermentPeriodIndicator: false,
          claimWipoDasIndicator: wipoDasOpen && wipoDasAccepted,
          designerWaiverIndicator: designerMode === "waiver",
          designers: designers.map(designer => ({
            designerName: designer.name,
            euipoDesignerIdentifier: designer.id || undefined
          }))
        }
      }
    ])

    setIsFormOpen(false)
    setDesignLimitError("")
    onChange(true)
  }

  const handleDeleteSavedDesign = async (id: string) => {
    if (disabled) return

    setSavedDesigns(prev => {
      const next = prev.filter(design => design.id !== id)
      setDesignLimitError("")
      if (editingSavedDesignId === id) {
        setEditingSavedDesignId(null)
        setEditingSavedDesignName("")
      }
      onChange(next.length > 0)
      return next
    })
  }

  const handleDeleteAllSavedDesigns = async () => {
    if (disabled) return

    setSavedDesigns([])
    setDesignLimitError("")
    setEditingSavedDesignId(null)
    setEditingSavedDesignName("")
    onChange(false)
  }

  const openEditSavedDesign = (design: SavedDesignCard) => {
    if (disabled) return

    setEditingSavedDesignId(design.id)
    setEditingSavedDesignName(design.name)
  }

  const saveEditedSavedDesign = (name: string) => {
    if (disabled) return
    if (!editingSavedDesignId) return

    setSavedDesigns(prev =>
      prev.map(design =>
        design.id === editingSavedDesignId
          ? { ...design, name }
          : design
      )
    )
    setEditingSavedDesignId(null)
    setEditingSavedDesignName("")
  }

  const moveDesignPreview = (designId: string, direction: "prev" | "next") => {
    setSavedDesigns(prev =>
      prev.map(design => {
        if (design.id !== designId || design.previewUrls.length === 0) {
          return design
        }

        const total = design.previewUrls.length
        const nextIndex =
          direction === "prev"
            ? (design.currentPreviewIndex - 1 + total) % total
            : (design.currentPreviewIndex + 1) % total

        return { ...design, currentPreviewIndex: nextIndex }
      })
    )
  }

  const handleAddProductIndication = () => {
    if (!selectedProductIndication) return

    setAddedProductIndications(prev => {
      const alreadyAdded = prev.some(term =>
        term.conceptId
          ? term.conceptId === selectedProductIndication.conceptId
          : formatProductIndication(term) === formatProductIndication(selectedProductIndication)
      )

      return alreadyAdded ? prev : [...prev, selectedProductIndication]
    })
    setProductIndication("")
    setSelectedProductIndication(null)
    setProductIndicationResults([])
  }

  const handleTransferDocumentSelect = (file: File) => {
    const isAccepted =
      file.type === "application/pdf" ||
      file.type === "image/jpeg" ||
      file.name.toLowerCase().endsWith(".pdf") ||
      file.name.toLowerCase().endsWith(".jpg") ||
      file.name.toLowerCase().endsWith(".jpeg")

    if (!isAccepted) {
      setTransferDocumentFile(null)
      setTransferDocumentError("Please upload a PDF or JPEG file.")
      return
    }

    if (file.size > 2 * 1024 * 1024) {
      setTransferDocumentFile(null)
      setTransferDocumentError("The file must not exceed 2 MB.")
      return
    }

    setTransferDocumentFile(file)
    setTransferDocumentError("")
  }

  const handlePriorityDocumentSelect = (file: File) => {
    const isAccepted =
      file.type === "application/pdf" ||
      file.type === "image/jpeg" ||
      file.name.toLowerCase().endsWith(".pdf") ||
      file.name.toLowerCase().endsWith(".jpg") ||
      file.name.toLowerCase().endsWith(".jpeg")

    if (!isAccepted) {
      setManualPriorityFile(null)
      setManualPriorityFileError("Please upload a PDF or JPEG file.")
      return
    }

    if (file.size > 2 * 1024 * 1024) {
      setManualPriorityFile(null)
      setManualPriorityFileError("The file must not exceed 2 MB.")
      return
    }

    setManualPriorityFile(file)
    setManualPriorityFileError("")
  }

  const resetPriorityEntry = () => {
    setPriorityOptionsOpen(false)
    setPriorityEntryMode(null)
    setTransferDocumentChecked(false)
    setTransferDocumentFile(null)
    setTransferDocumentError("")
    setDatabasePriorityCountry("")
    setDatabasePriorityId("")
    setDatabasePriorityResults([])
    setDatabasePrioritySelected(null)
    setDatabasePriorityOpen(false)
    setDatabasePriorityLoading(false)
    setDatabasePriorityError("")
    setManualPriorityCountry("")
    setManualPriorityDate("")
    setManualPriorityNumber("")
    setManualPriorityFile(null)
    setManualPriorityFileError("")
  }

  const selectPriorityEntryMode = (mode: "wipo" | "manual" | "database") => {
    setPriorityEntryMode(mode)

    if (mode !== "wipo") {
      setTransferDocumentChecked(false)
      setTransferDocumentFile(null)
      setTransferDocumentError("")
    }

    if (mode !== "database") {
      setDatabasePriorityCountry("")
      setDatabasePriorityId("")
      setDatabasePriorityResults([])
      setDatabasePrioritySelected(null)
      setDatabasePriorityOpen(false)
      setDatabasePriorityLoading(false)
      setDatabasePriorityError("")
    } else {
      setDatabasePriorityCountry("EUIPO")
    }
  }

  const isManualPriorityValid =
    manualPriorityCountry &&
    manualPriorityDate &&
    manualPriorityNumber &&
    manualPriorityFile &&
    !manualPriorityFileError

  const handleImportDatabasePriority = async () => {
    const designNumber = databasePriorityId.trim()
    if (!designNumber) return

    try {
      setDatabasePriorityLoading(true)
      setDatabasePriorityError("")
      const details = await getDesignDetails(designNumber)
      setDatabasePrioritySelected(details)
      setDatabasePriorityId(details.designNumber)
      setDatabasePriorityCountry("EUIPO")
      setDatabasePriorityResults([])
      setDatabasePriorityOpen(false)
    } catch {
      setDatabasePrioritySelected(null)
      setDatabasePriorityError("Design could not be imported.")
      setDatabasePriorityOpen(false)
    } finally {
      setDatabasePriorityLoading(false)
    }
  }

  const handleAddManualPriority = async () => {
    if (!isManualPriorityValid || addingPriority) return

    try {
      setAddingPriority(true)
      setAddPriorityError("")

      const filingCopy = manualPriorityFile
        ? await uploadDocument(manualPriorityFile, "PRIORITY_COPY")
        : null
      const transferAssignment = transferDocumentFile
        ? await uploadDocument(transferDocumentFile, "PRIORITY_COPY")
        : null

      setPriorityClaims(prev => [
        ...prev,
        {
          country: manualPriorityCountry,
          applicationNumber: manualPriorityNumber.trim(),
          applicationDate: manualPriorityDate,
          copyFirstFilingDocument: filingCopy?.euipoDocumentId || undefined,
          transferAssignmentDocument:
            transferAssignment?.euipoDocumentId || undefined
        }
      ])

      resetPriorityEntry()
    } catch (error) {
      setAddPriorityError(
        formatApiError(
          error,
          "The priority claim could not be added. Please try again."
        )
      )
    } finally {
      setAddingPriority(false)
    }
  }

  const handleAddDatabasePriority = () => {
    if (!databasePrioritySelected) return

    setPriorityClaims(prev => [
      ...prev,
      {
        country: "EM",
        applicationNumber:
          databasePrioritySelected.applicationNumber ||
          databasePrioritySelected.designNumber,
        applicationDate: databasePrioritySelected.applicationDate
      }
    ])

    resetPriorityEntry()
  }

  const handleRemovePriorityClaim = (index: number) => {
    setPriorityClaims(prev => prev.filter((_, itemIndex) => itemIndex !== index))
  }



  const openNameModal = () => {
    setIsNameModalOpen(true)
  }

  const handleAddDesigner = () => {
    const nextName = designerName.trim()
    if (!nextName) return

    setDesigners(prev => [
      ...prev,
      {
        id: "",
        name: nextName
      }
    ])
    setDesignerName("")
  }

  const handleAddDatabaseDesigner = () => {
    if (!selectedDesigner) return

    setDesigners(prev => {
      const alreadyAdded = prev.some(designer =>
        designer.id === selectedDesigner.designerIdentifier
      )

      if (alreadyAdded) return prev

      return [
        ...prev,
        {
          id: selectedDesigner.designerIdentifier,
          name: selectedDesigner.name
        }
      ]
    })

    setDesignerSearch("")
    setSelectedDesigner(null)
    setDesignerSearchResults([])
    setDesignerDatabaseOpen(false)
  }

  const resetDesignerSearch = () => {
    setDesignerSearch("")
    setSelectedDesigner(null)
    setDesignerSearchResults([])
    setDesignerSearchError("")
    setDesignerDatabaseOpen(false)
  }

  const openEditDesigner = (index: number) => {
    setEditingDesignerIndex(index)
    setEditingDesignerName(designers[index].name)
  }

  const saveEditedDesigner = () => {
    const nextName = editingDesignerName.trim()
    if (!nextName || editingDesignerIndex === null) return

    setDesigners(prev =>
      prev.map((designer, index) =>
        index === editingDesignerIndex
          ? { ...designer, name: nextName }
          : designer
      )
    )
    setEditingDesignerIndex(null)
    setEditingDesignerName("")
  }

  return (
    <div className={`m-0 min-w-0 border-0 p-0 ${disabled ? "opacity-75" : ""}`}>
    <section
      id="designs"
      className="scroll-mt-32 border-t border-gray-300 pt-4"
    >
      <div className="mb-4">
        <SectionHeader
          icon={
            <img
              src="/bildmarke2.svg"
              alt="Design icon"
              className="w-6 h-6 shrink-0 brightness-0"
            />
          }
          title="Designs"
          subtitle="Add designs to your application."
        />
      </div>

      <div className="flex w-full items-center justify-center rounded-2xl border border-gray-200 bg-gray-50 p-6">
        {designLimitError && (
          <p className="mb-4 text-sm font-semibold text-red-600">
            {designLimitError}
          </p>
        )}
        {!isFormOpen && (
          <SavedDesignList
            savedDesigns={savedDesigns}
            hasReachedDesignLimit={hasReachedDesignLimit}
            onAddDesign={handleOpen}
            onDeleteAll={handleDeleteAllSavedDesigns}
            onEditDesign={openEditSavedDesign}
            onDeleteDesign={handleDeleteSavedDesign}
            onMovePreview={moveDesignPreview}
            disabled={disabled}
          />
        )}

        {editingSavedDesignId && !disabled && (
          <DesignNameDialog
            initialName={editingSavedDesignName}
            onCancel={() => {
              setEditingSavedDesignId(null)
              setEditingSavedDesignName("")
            }}
            onSave={(name) => {
              saveEditedSavedDesign(name)
            }}
          />
        )}

        {isFormOpen && (
          <div className="rounded bg-white shadow-sm overflow-visible">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {designName}
                </h3>
                <button
                  type="button"
                  onClick={openNameModal}
                  className="text-blue-800 hover:text-blue-700"
                >
                  <FiEdit2 className="h-4 w-4" />
                </button>
              </div>

              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-500 text-base font-semibold leading-none text-white hover:bg-gray-600"
              >
                ×
              </button>
            </div>

            {isNameModalOpen && (
              <DesignNameDialog
                initialName={designName}
                onCancel={() => setIsNameModalOpen(false)}
                onSave={(name) => {
                  setDesignName(name)
                  setIsNameModalOpen(false)
                }}
              />
            )}

            <div className="p-6">
              <div className="mb-6 flex items-center gap-2">
                <h4 className="text-lg font-semibold text-blue-800">
                  Views
                </h4>
                <InfoBox title="Design views">
                  Upload up to 10 views for this design. The first view is usually
                  shown in search result lists.
                </InfoBox>
              </div>

              <DesignUploadBox
                uploadedFiles={uploadedFiles}
                onFilesSelect={handleDesignFilesSelect}
                uploadStateByFile={designViewUploads}
                disabled={disabled}
              />

              {hasDesignViewUploadInProgress && (
                <p className="mt-3 text-sm font-semibold text-gray-600">
                  <SpinnerText>Uploading design views to EUIPO...</SpinnerText>
                </p>
              )}

              {(designViewUploadError || hasDesignViewUploadError) && (
                <p className="mt-3 text-sm font-semibold text-red-600">
                  {designViewUploadError || "One or more design views could not be uploaded."}
                </p>
              )}

              <DefermentSection
                deferment={deferment}
                setDeferment={setDeferment}
              />


              <ProductIndicationSection
                productIndication={productIndication}
                setProductIndication={setProductIndication}
                productIndicationResults={productIndicationResults}
                setProductIndicationResults={setProductIndicationResults}
                selectedProductIndication={selectedProductIndication}
                setSelectedProductIndication={setSelectedProductIndication}
                addedProductIndications={addedProductIndications}
                setAddedProductIndications={setAddedProductIndications}
                productIndicationValidation={productIndicationValidation}
                productIndicationLoading={productIndicationLoading}
                productIndicationError={productIndicationError}
                formatProductIndication={formatProductIndication}
                handleAddProductIndication={handleAddProductIndication}
              />


              <DesignerDetailsSection
                designerDetailsOpen={designerDetailsOpen}
                setDesignerDetailsOpen={setDesignerDetailsOpen}
                designerMode={designerMode}
                setDesignerMode={setDesignerMode}
                designerName={designerName}
                setDesignerName={setDesignerName}
                designers={designers}
                setDesigners={setDesigners}
                designerSearch={designerSearch}
                setDesignerSearch={setDesignerSearch}
                designerSearchResults={designerSearchResults}
                setDesignerSearchResults={setDesignerSearchResults}
                selectedDesigner={selectedDesigner}
                setSelectedDesigner={setSelectedDesigner}
                designerDatabaseOpen={designerDatabaseOpen}
                setDesignerDatabaseOpen={setDesignerDatabaseOpen}
                designerSearchLoading={designerSearchLoading}
                designerSearchError={designerSearchError}
                editingDesignerIndex={editingDesignerIndex}
                setEditingDesignerIndex={setEditingDesignerIndex}
                editingDesignerName={editingDesignerName}
                setEditingDesignerName={setEditingDesignerName}
                resetDesignerSearch={resetDesignerSearch}
                handleAddDesigner={handleAddDesigner}
                handleAddDatabaseDesigner={handleAddDatabaseDesigner}
                openEditDesigner={openEditDesigner}
                saveEditedDesigner={saveEditedDesigner}
              />


              <DescriptionSection
                descriptionOpen={descriptionOpen}
                setDescriptionOpen={setDescriptionOpen}
                description={description}
                setDescription={setDescription}
              />


              <DesignClaimsSection
                priorityClaims={priorityClaims}
                claimsOpen={claimsOpen}
                setClaimsOpen={setClaimsOpen}
                priorityOptionsOpen={priorityOptionsOpen}
                setPriorityOptionsOpen={setPriorityOptionsOpen}
                priorityEntryMode={priorityEntryMode}
                setPriorityEntryMode={setPriorityEntryMode}
                resetPriorityEntry={resetPriorityEntry}
                selectPriorityEntryMode={selectPriorityEntryMode}
                transferDocumentChecked={transferDocumentChecked}
                setTransferDocumentChecked={setTransferDocumentChecked}
                transferDocumentFile={transferDocumentFile}
                setTransferDocumentFile={setTransferDocumentFile}
                transferDocumentError={transferDocumentError}
                setTransferDocumentError={setTransferDocumentError}
                handleTransferDocumentSelect={handleTransferDocumentSelect}
                manualPriorityCountry={manualPriorityCountry}
                setManualPriorityCountry={setManualPriorityCountry}
                manualPriorityDate={manualPriorityDate}
                setManualPriorityDate={setManualPriorityDate}
                manualPriorityNumber={manualPriorityNumber}
                setManualPriorityNumber={setManualPriorityNumber}
                manualPriorityFile={manualPriorityFile}
                setManualPriorityFile={setManualPriorityFile}
                manualPriorityFileError={manualPriorityFileError}
                setManualPriorityFileError={setManualPriorityFileError}
                handlePriorityDocumentSelect={handlePriorityDocumentSelect}
                isManualPriorityValid={isManualPriorityValid}
                databasePriorityCountry={databasePriorityCountry}
                databasePriorityId={databasePriorityId}
                setDatabasePriorityId={setDatabasePriorityId}
                databasePriorityResults={databasePriorityResults}
                setDatabasePriorityResults={setDatabasePriorityResults}
                databasePrioritySelected={databasePrioritySelected}
                setDatabasePrioritySelected={setDatabasePrioritySelected}
                databasePriorityOpen={databasePriorityOpen}
                setDatabasePriorityOpen={setDatabasePriorityOpen}
                databasePriorityLoading={databasePriorityLoading}
                databasePriorityError={databasePriorityError}
                handleImportDatabasePriority={handleImportDatabasePriority}
                addingPriority={addingPriority}
                addPriorityError={addPriorityError}
                handleAddManualPriority={handleAddManualPriority}
                handleAddDatabasePriority={handleAddDatabasePriority}
                handleRemovePriorityClaim={handleRemovePriorityClaim}
              />


              <WipoDasSection
                wipoDasOpen={wipoDasOpen}
                setWipoDasOpen={setWipoDasOpen}
                wipoDasAccepted={wipoDasAccepted}
                setWipoDasAccepted={setWipoDasAccepted}
              />
            </div>

            <div className="flex justify-end gap-8 border-t border-gray-100 px-6 py-5">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-semibold uppercase text-blue-800 hover:text-blue-700"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleAdd}
                disabled={!canAddDesign || hasReachedDesignLimit}
                className={`rounded border-2 px-8 py-2 text-sm font-semibold uppercase text-white ${
                  canAddDesign && !hasReachedDesignLimit
                    ? "border-blue-800 bg-blue-800 hover:bg-blue-700"
                    : "border-gray-500 bg-gray-400 cursor-not-allowed"
                }`}
              >
                {savingDesign ? <SpinnerText>Saving...</SpinnerText> : "Add design"}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
    </div>
  )
}
